import { ArrowLeft, ArrowRight, ExternalLink, Filter, Package2, Percent, Search, Tag, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import type { Product } from '../../hooks/useProductSystem';
import { useProductSystem } from '../../hooks/useProductSystem';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { ProductDetails } from '../products/ProductDetails';
import { useMiniAppModal, MiniAppFilters } from '../../hooks/useMiniAppModal';

// List of valid currency codes
const VALID_CURRENCIES = ['EUR', 'USD', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY'];

// Mise à jour de l'interface FilterState pour inclure les IDs de produits mentionnés
interface FilterState {
  category: string | null;
  tags: Set<string>;
  search: string;
  mentionedIds?: string[];
}

export function ProductsApp() {
  const { chatbotPublicId } = useChatbot();
  const { products, categories, tags, settings, loading, error, initialize } = useProductSystem();
  const { colors } = useDesignColors();
  const { theme } = useThemeSettings();
  const { settings: multilingualSettings } = useMultilingualSettings();
  const { filters } = useMiniAppModal();
  
  // Récupérer les filtres de produits s'ils existent
  const productFilters = filters.products || {};
  
  // Vérifier si on a un filtre spécifique pour les produits mentionnés
  const hasExplicitMentionedIds = 
    productFilters.mentionedIds && 
    Array.isArray(productFilters.mentionedIds) && 
    productFilters.mentionedIds.length > 0;
  
  // Si le tableau est vide, c'est qu'on a ouvert la mini-app normalement via le bouton
  const shouldApplyMentionedFilter = hasExplicitMentionedIds;
  
  const initialSearch = productFilters.search || '';
  const initialCategory = productFilters.category || null;
  const initialTags = productFilters.tags || new Set<string>();
  const mentionedProductIds = shouldApplyMentionedFilter ? productFilters.mentionedIds || [] : [];
  
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(initialCategory);
  const [selectedTags, setSelectedTags] = useState<Set<string>>(initialTags);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isDark = theme.mode === 'dark';
  
  // mais uniquement si aucun produit n'a déjà été explicitement sélectionné ou fermé
  const [productModalClosed, setProductModalClosed] = useState(false);

  const hasActiveFilters = selectedCategory || selectedTags.size > 0 || searchTerm || mentionedProductIds.length > 0;
  
  console.log('État des filtres dans ProductsApp:', {
    hasExplicitMentionedIds,
    shouldApplyMentionedFilter,
    mentionedProductIds,
    productFilters
  });

  // Get translations for the current language
  const currentLang = multilingualSettings.currentLanguage || 'en';
  const translations = multilingualSettings.translations[currentLang];
  const productTranslations = translations?.miniApps?.find(app => app.id === 'products')?.content?.productDetails || {};

  // Function to get translated product data
  const getTranslatedProduct = (product: Product) => {
    const translation = productTranslations[product.id];
    return {
      ...product,
      name: translation?.name || product.name,
      description: translation?.description || product.description,
      category: translation?.category || product.category,
      tags: translation?.tags || product.tags,
    };
  };

  // Function to format price with valid currency
  const formatPrice = (price: number, currency?: string) => {
    try {
      // Ensure currency is a valid ISO 4217 currency code
      const validCurrency = VALID_CURRENCIES.includes(currency?.toUpperCase() || '') 
        ? currency?.toUpperCase() 
        : 'EUR';
      
      return new Intl.NumberFormat('en-EN', {
        style: 'currency',
        currency: validCurrency,
        maximumFractionDigits: 0
      }).format(price);
    } catch (err) {
      // Fallback formatting if currency formatting fails
      return `${price} EUR`;
    }
  };

  // Function to display the number of active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedCategory) count++;
    count += selectedTags.size;
    if (searchTerm) count++;
    if (mentionedProductIds.length > 0) count++;
    return count;
  };

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      if (!newIsMobile) {
        setShowSidebar(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (chatbotPublicId) {
      initialize(chatbotPublicId);
    }
  }, [chatbotPublicId, initialize]);

  useEffect(() => {
    if (initialSearch) {
      setSearchTerm(initialSearch);
    }
    if (initialCategory) {
      setSelectedCategory(initialCategory);
    }
    if (initialTags.size > 0) {
      setSelectedTags(new Set(initialTags));
    }
  }, [initialSearch, initialCategory, initialTags]);

  const filteredProducts = products
    .filter(product => {
      // Si nous avons des IDs de produits mentionnés et que ce produit n'en fait pas partie, filtrer
      if (shouldApplyMentionedFilter && mentionedProductIds.length > 0 && !mentionedProductIds.includes(product.id)) {
        return false;
      }
      if (searchTerm && !getTranslatedProduct(product).name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (selectedCategory && product.category !== selectedCategory) {
        return false;
      }
      if (selectedTags.size > 0 && !product.tags?.some(tag => selectedTags.has(tag))) {
        return false;
      }
      return true;
    })
    .map(getTranslatedProduct);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory(null);
    setSelectedTags(new Set());
  };

  const handleCategoryClick = (categoryName: string) => {
    setSelectedCategory(prev => prev === categoryName ? null : categoryName);
  };

  const handleTagClick = (tagName: string) => {
    setSelectedTags(prev => {
      const newTags = new Set(prev);
      if (newTags.has(tagName)) {
        newTags.delete(tagName);
      } else {
        newTags.add(tagName);
      }
      return newTags;
    });
  };

  // Si nous avons des IDs spécifiques mentionnés, afficher automatiquement le premier produit
  // mais uniquement si aucun produit n'a déjà été explicitement sélectionné ou fermé
  useEffect(() => {
    // Uniquement ouvrir automatiquement si on n'a pas explicitement fermé la modal
    if (mentionedProductIds.length > 0 && filteredProducts.length > 0 && !selectedProduct && !productModalClosed) {
      setSelectedProduct(filteredProducts[0]);
    }
  }, [mentionedProductIds, filteredProducts, selectedProduct, productModalClosed]);
  
  const handleCloseProductDetails = () => {
    setSelectedProduct(null);
    // Marquer que l'utilisateur a explicitement fermé la fiche
    setProductModalClosed(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8  border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 text-center p-4">
        {error}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center p-8">
        <div 
          className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)` }}
        >
          <Package2 
            className="w-8 h-8"
            style={{ color: colors.gradientStart }}
          />
        </div>
        <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          {translations?.noProductsMessage || 'No products available'}
        </p>
      </div>
    );
  }

  // Si c'est un filtre par produits mentionnés et qu'on n'a qu'un seul produit, afficher directement les détails
  // Mais uniquement si la fiche n'a pas été explicitement fermée
  if (mentionedProductIds.length > 0 && filteredProducts.length === 1 && !selectedProduct && !productModalClosed) {
    return <ProductDetails product={filteredProducts[0]} onClose={handleCloseProductDetails} />;
  }

  return (
    <div className="w-full h-[500px] flex relative">
      {/* Sidebar avec filtres */}
      <div 
        className={`
          ${isMobile ? 'fixed inset-y-0 left-0 z-50 w-full max-w-sm' : 'w-64'} 
          transform transition-transform duration-300 ease-in-out
          ${isMobile && !showSidebar ? '-translate-x-full' : 'translate-x-0'}
          ${isDark ? 'bg-gray-900/95' : 'bg-white/95'} 
          backdrop-blur-xl
          border-r ${isDark ? 'border-white/10' : 'border-gray-200'}
          overflow-hidden
          flex flex-col
        `}
      >
        {isMobile && (
          <div className="flex justify-between items-center p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSidebar(false)}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </button>
              <h3 className="text-white font-medium">Filters</h3>
            </div>
            {hasActiveFilters && (
              <button
                onClick={() => setShowSidebar(false)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm"
                style={{
                  background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                }}
              >
                <span className="text-white">See {filteredProducts.length} products</span>
                <ArrowRight className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        )}

        <div className="p-4 h-full overflow-y-auto">
          <div className="space-y-4">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={translations?.searchPlaceholder || "Search for a product..."}
                className={`w-full pl-10 pr-4 py-2 rounded-lg transition-colors ${
                  isDark 
                    ? 'bg-black/20 border-white/5 text-white placeholder-gray-500' 
                    : 'bg-white/80 border-gray-200 text-gray-900 placeholder-gray-400'
                } border focus:outline-none focus:ring-2 focus:ring-indigo-500`}
              />
            </div>

            {/* Filtres produits mentionnés */}
            {mentionedProductIds.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                <div 
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                    color: colors.gradientStart
                  }}
                >
                  <span>Mentioned products</span>
                </div>
              </div>
            )}

            {/* Filtres actifs */}
            {(selectedCategory || selectedTags.size > 0 || searchTerm) && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
                {selectedCategory && (
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>{selectedCategory}</span>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                {Array.from(selectedTags).map(tag => (
                  <div 
                    key={tag} 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>{tag}</span>
                    <button
                      onClick={() => handleTagClick(tag)}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {searchTerm && (
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                    style={{
                      backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                      color: colors.gradientStart
                    }}
                  >
                    <span>"{searchTerm}"</span>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="p-0.5 hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <button
                  onClick={resetFilters}
                  className="px-2 py-1 rounded-lg text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                >
                  Reset
                </button>
              </div>
            )}

            {/* Catégories */}
            {categories.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {translations?.categoryLabel || 'Categories'}
                </h4>
                <div className="space-y-1">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category.name)}
                      className={`w-full px-3 py-2 rounded-lg flex items-center justify-between transition-all ${
                        selectedCategory === category.name
                          ? 'bg-gradient-to-r'
                          : isDark
                            ? 'hover:bg-white/5 text-gray-300'
                            : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      style={selectedCategory === category.name ? {
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                        color: colors.gradientStart
                      } : undefined}
                    >
                      <span>{category.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-3 ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {translations?.tagsLabel || 'Tags'}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <button
                      key={tag.id}
                      onClick={() => handleTagClick(tag.name)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm transition-all ${
                        selectedTags.has(tag.name)
                          ? 'bg-gradient-to-r'
                          : isDark
                            ? 'bg-white/5 text-gray-300 hover:bg-white/10'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      style={selectedTags.has(tag.name) ? {
                        backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                        color: colors.gradientStart
                      } : undefined}
                    >
                      <Tag className="w-3 h-3" />
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay pour mobile */}
      {isMobile && showSidebar && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={() => setShowSidebar(false)}
        />
      )}

      {/* Liste des produits */}
      <div className={`flex-1 h-full overflow-y-auto ${
        isDark ? 'bg-transparent' : 'bg-white'
      }`}>
        <div className="p-4">
          <div className={`grid grid-cols-1 gap-4`}>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={`group relative overflow-hidden rounded-xl border transition-all ${
                  isDark 
                    ? 'bg-gray-800/50 border-white/5 hover:border-indigo-500/50' 
                    : 'bg-white/50 border-black/5 hover:border-indigo-500/50'
                }`}
              >
                <button
                  onClick={() => setSelectedProduct(product)}
                  className="w-full text-left"
                >
                  <div className="flex gap-4 p-4">
                    {product.image_url && (
                      <div className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-black/20 group-hover:scale-105 transition-transform shrink-0">
                        <img
                          src={product.image_url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                        {product.discount && (
                          <div 
                            className="absolute top-2 right-2 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold text-white"
                            style={{
                              background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
                            }}
                          >
                            -{product.discount}%
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <h4 className={`text-base sm:text-lg font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.name}</h4>
                        {product.metadata?.redirect_url && (
                          <a
                            href={String(product.metadata.redirect_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 p-1 text-gray-400 hover:text-white transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                      
                      {product.description && (
                        <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{product.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 mt-3">
                        {settings.showPrices && product.price && (
                          <div 
                            className="text-base sm:text-lg font-medium bg-clip-text text-transparent bg-gradient-to-r"
                            style={{
                              backgroundImage: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`
                            }}
                          >
                            {product.discount ? (
                              <>
                                <Percent className="w-3 h-3" />
                                {formatPrice(product.price * (1 - product.discount / 100), product.currency)}
                              </>
                            ) : (
                              formatPrice(product.price, product.currency)
                            )}
                          </div>
                        )}
                        {settings.showStock && (
                          <span 
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm"
                            style={{
                              backgroundImage: `linear-gradient(to right, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                              color: colors.gradientStart
                            }}
                          >
                            <Package2 className="w-3 h-3" />
                            Stock: {product.stock || 0}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mt-3">
                        {product.category && (
                          <span className={`px-3 py-1.5 rounded-lg text-sm ${
                            isDark 
                              ? 'bg-white/5 text-gray-300' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {product.category}
                          </span>
                        )}
                        {/* Affichage des tags */}
                        {product.tags?.map((tag, index) => (
                          <span key={index} className={`px-3 py-1.5 rounded-lg text-sm ${
                            isDark 
                              ? 'bg-white/5 text-gray-300' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bouton toggle filtres mobile avec badge */}
      {isMobile && !showSidebar && (
        <button
          onClick={() => setShowSidebar(true)}
          className="fixed bottom-4 left-4 p-3 rounded-full shadow-lg z-40 flex items-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
          }}
        >
          <Filter className="w-5 h-5 text-white" />
          {getActiveFiltersCount() > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {getActiveFiltersCount()}
            </span>
          )}
        </button>
      )}

      {/* Modal détails produit */}
      {selectedProduct && (
        <ProductDetails 
          product={selectedProduct} 
          onClose={handleCloseProductDetails}
        />
      )}
    </div>
  );
}